import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IconButton, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

interface FieldMapping {
  [key: string]: string; // dynamic keys
}

interface LabelMapping {
  [key: string]: string;
}

interface GetReviewColumnsProps {
  onEdit: (row: any) => void;
  onDelete: (ids: string[]) => void;
  onDisapprove: (ids: string[]) => void;
  onApprove: (ids: string[]) => void;
  fields: FieldMapping; // required since logic depends on it
  labels?: LabelMapping;
  hasPermission: (module: string, action: string) => boolean;
  review:string;
}

export const getReviewColumns = ({
  onEdit,
  onDelete,
  onDisapprove,
  onApprove,
  fields,
  labels = {},
  hasPermission,
  review
}: GetReviewColumnsProps): GridColDef[] => {
  const idField = fields.id || 'id';
  const reviewStatusField = fields.reviewStatus || 'reviewApprovalStatus';

  const columns = Object.entries(fields).map(([key, value]) => {
    const label = labels[key] || key;

    // Handle special fields
    if (key === 'view') {
      if (!hasPermission(review, 'View')) return null;
      return {
        field: value,
        headerName: label,
        flex: 0.5,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <IconButton size="small" onClick={() => onEdit(params.row)} color="secondary">
            <VisibilityIcon fontSize="small" />
          </IconButton>
        ),
      };
    }

    if (key === 'status') {
      const canApprove = hasPermission(review, 'unblock');
      const canDisapprove = hasPermission(review, 'block');
      if (!canApprove && !canDisapprove) return null;

      return {
        field: value,
        headerName: label,
        flex: 0.8,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const isApproved = params.row?.[reviewStatusField];
          const rowId = params.row?.[idField];

          return !isApproved ? (
            canApprove && (
              <Button
                variant="contained"
                size="small"
                color="warning"
                onClick={() => onApprove([rowId])}
                startIcon={<ThumbDownIcon />}
                sx={{
                  textTransform: 'none',
                }}
              >
                {labels.disapprove || 'Disapprove'}
              </Button>
            )
          ) : (
            canDisapprove && (
              <Button
                variant="contained"
                size="small"
                color="success"
                onClick={() => onDisapprove([rowId])}
                startIcon={<ThumbUpIcon />}
                sx={{
                  textTransform: 'none',
                }}
              >
               {labels.approve || 'Approve'}
              </Button>
            )
          );
        },
      };
    }

    if (key === 'delete') {
      if (!hasPermission(review, 'Delete')) return null;
      return {
        field: value,
        headerName: label,
        flex: 0.5,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const rowId = params.row?.[idField];
          return (
            <IconButton color="error" onClick={() => onDelete([rowId])}>
              <DeleteIcon />
            </IconButton>
          );
        },
      };
    }

    // Default column
    return {
      field: value,
      headerName: label,
      flex: 1,
    };
  });

  return columns.filter(Boolean) as GridColDef[];
};
